import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Search, X, Music, Calendar, Truck, Store, Filter, User, SlidersHorizontal } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

const API = "/api";

const CATEGORIES = [
  { value: '', label: 'All Categories', icon: Search },
  { value: 'artists', label: 'Artists', icon: User },
  { value: 'events', label: 'Events', icon: Calendar },
  { value: 'food_trucks', label: 'Food Trucks', icon: Truck },
  { value: 'vendors', label: 'Vendors', icon: Store },
  { value: 'tracks', label: 'Music', icon: Music },
];

const PRICE_TIERS = [
  { value: '', label: 'Any Price' },
  { value: 'free', label: 'Free' },
  { value: '$', label: '$' },
  { value: '$$', label: '$$' },
  { value: '$$$', label: '$$$' },
];

const VIBES = [
  { value: '', label: 'Any Vibe' },
  { value: 'casual', label: 'Casual' },
  { value: 'upbeat', label: 'Upbeat' },
  { value: 'chill', label: 'Chill' },
  { value: 'energetic', label: 'Energetic' },
  { value: 'intimate', label: 'Intimate' },
];

const CUISINES = [
  { value: '', label: 'Any Cuisine' },
  { value: 'mexican', label: 'Mexican' },
  { value: 'asian', label: 'Asian' },
  { value: 'american', label: 'American' },
  { value: 'bbq', label: 'BBQ' },
  { value: 'mediterranean', label: 'Mediterranean' },
  { value: 'vegan', label: 'Vegan' },
];

const GENRES = [
  { value: '', label: 'Any Genre' },
  { value: 'hip hop', label: 'Hip Hop' },
  { value: 'rock', label: 'Rock' },
  { value: 'indie', label: 'Indie' },
  { value: 'jazz', label: 'Jazz' },
  { value: 'electronic', label: 'Electronic' },
  { value: 'folk', label: 'Folk' },
  { value: 'r&b', label: 'R&B' },
  { value: 'pop', label: 'Pop' },
  { value: 'funk', label: 'Funk' },
];

const SearchBar = ({ onResults, showFilters = true, placeholder = "Search artists, events, food..." }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    genre: '',
    price_tier: '',
    vibe: '',
    cuisine: ''
  });
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const activeFiltersCount = Object.values(filters).filter(v => v).length;

  const fetchSuggestions = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/search/suggestions?q=${encodeURIComponent(searchQuery)}`);
      setSuggestions(response.data);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    }
  }, []);

  const performSearch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      if (filters.category) params.append('category', filters.category);
      if (filters.genre) params.append('genre', filters.genre);
      if (filters.price_tier) params.append('price_tier', filters.price_tier);
      if (filters.vibe) params.append('vibe', filters.vibe);
      if (filters.cuisine) params.append('cuisine', filters.cuisine);

      const response = await axios.get(`${API}/search?${params.toString()}`);
      if (onResults) {
        onResults(response.data);
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  }, [query, filters, onResults]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(query);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, fetchSuggestions]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    setShowSuggestions(false);
    performSearch();
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion.text);
    setShowSuggestions(false);
    
    // Navigate based on suggestion type
    if (suggestion.type === 'genre') {
      setFilters({ ...filters, genre: suggestion.text });
    }
    
    // Trigger search
    setTimeout(() => performSearch(), 100);
  };

  const clearFilters = () => {
    setFilters({ category: '', genre: '', price_tier: '', vibe: '', cuisine: '' });
  };

  const clearSearch = () => {
    setQuery('');
    setSuggestions([]);
    clearFilters();
    if (onResults) onResults(null);
  };

  const getIcon = (type) => {
    switch (type) {
      case 'artist': return <User size={14} className="text-primary" />;
      case 'event': return <Calendar size={14} className="text-secondary" />;
      case 'food_truck': return <Truck size={14} className="text-orange-500" />;
      case 'genre': return <Music size={14} className="text-purple-500" />;
      default: return <Search size={14} />;
    }
  };

  return (
    <div className="w-full space-y-3">
      <form onSubmit={handleSubmit} className="relative">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              placeholder={placeholder}
              className="pl-10 pr-10 h-11"
            />
            {query && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X size={16} />
              </button>
            )}
            
            {/* Suggestions Dropdown */}
            <AnimatePresence>
              {showSuggestions && suggestions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-lg z-50 overflow-hidden"
                >
                  {suggestions.map((suggestion, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="w-full px-4 py-2 text-left hover:bg-muted flex items-center gap-2 text-sm"
                    >
                      {getIcon(suggestion.type)}
                      <span>{suggestion.text}</span>
                      <span className="text-xs text-muted-foreground ml-auto capitalize">{suggestion.type.replace('_', ' ')}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {showFilters && (
            <Popover open={showFilterPanel} onOpenChange={setShowFilterPanel}>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" className="gap-2 relative h-11">
                  <SlidersHorizontal size={16} />
                  <span className="hidden sm:inline">Filters</span>
                  {activeFiltersCount > 0 && (
                    <Badge variant="secondary" className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                      {activeFiltersCount}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-4" align="end">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Filters</h4>
                    {activeFiltersCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={clearFilters}>Clear all</Button>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-sm text-muted-foreground">Category</label>
                      <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
                        <SelectTrigger><SelectValue placeholder="All Categories" /></SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value || 'all'}>
                              <div className="flex items-center gap-2">
                                <cat.icon size={14} />
                                {cat.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <label className="text-sm text-muted-foreground">Genre</label>
                      <Select value={filters.genre} onValueChange={(v) => setFilters({ ...filters, genre: v })}>
                        <SelectTrigger><SelectValue placeholder="Any Genre" /></SelectTrigger>
                        <SelectContent>
                          {GENRES.map((genre) => (
                            <SelectItem key={genre.value} value={genre.value || 'any'}>{genre.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <label className="text-sm text-muted-foreground">Price Range</label>
                      <Select value={filters.price_tier} onValueChange={(v) => setFilters({ ...filters, price_tier: v })}>
                        <SelectTrigger><SelectValue placeholder="Any Price" /></SelectTrigger>
                        <SelectContent>
                          {PRICE_TIERS.map((price) => (
                            <SelectItem key={price.value} value={price.value || 'any'}>{price.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <label className="text-sm text-muted-foreground">Vibe</label>
                      <Select value={filters.vibe} onValueChange={(v) => setFilters({ ...filters, vibe: v })}>
                        <SelectTrigger><SelectValue placeholder="Any Vibe" /></SelectTrigger>
                        <SelectContent>
                          {VIBES.map((vibe) => (
                            <SelectItem key={vibe.value} value={vibe.value || 'any'}>{vibe.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <label className="text-sm text-muted-foreground">Cuisine (Food)</label>
                      <Select value={filters.cuisine} onValueChange={(v) => setFilters({ ...filters, cuisine: v })}>
                        <SelectTrigger><SelectValue placeholder="Any Cuisine" /></SelectTrigger>
                        <SelectContent>
                          {CUISINES.map((cuisine) => (
                            <SelectItem key={cuisine.value} value={cuisine.value || 'any'}>{cuisine.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button onClick={() => { handleSubmit(); setShowFilterPanel(false); }} className="w-full">
                    Apply Filters
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          )}

          <Button type="submit" disabled={loading} className="h-11">
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </div>
      </form>

      {/* Active Filters Display */}
      {activeFiltersCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {filters.category && (
            <Badge variant="secondary" className="gap-1">
              {CATEGORIES.find(c => c.value === filters.category)?.label}
              <X size={12} className="cursor-pointer" onClick={() => setFilters({ ...filters, category: '' })} />
            </Badge>
          )}
          {filters.genre && (
            <Badge variant="secondary" className="gap-1">
              {filters.genre}
              <X size={12} className="cursor-pointer" onClick={() => setFilters({ ...filters, genre: '' })} />
            </Badge>
          )}
          {filters.price_tier && (
            <Badge variant="secondary" className="gap-1">
              {filters.price_tier}
              <X size={12} className="cursor-pointer" onClick={() => setFilters({ ...filters, price_tier: '' })} />
            </Badge>
          )}
          {filters.vibe && (
            <Badge variant="secondary" className="gap-1">
              {filters.vibe}
              <X size={12} className="cursor-pointer" onClick={() => setFilters({ ...filters, vibe: '' })} />
            </Badge>
          )}
          {filters.cuisine && (
            <Badge variant="secondary" className="gap-1">
              {filters.cuisine}
              <X size={12} className="cursor-pointer" onClick={() => setFilters({ ...filters, cuisine: '' })} />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
